import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { CustomDropdown } from '@medprompt/shared';
import MicrophoneCheckService from '../services/MicrophoneCheckService';

const RecordingMicPicker = ({
  selectedDeviceId,
  disabled,
  disabledReason,
  onSelectDevice
}) => {
  const [devices, setDevices] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const inputs = await MicrophoneCheckService.enumerateAudioInputs();
        if (!cancelled) setDevices(inputs || []);
      } catch (err) {
        console.error('Failed to load microphones for recording picker:', err);
      }
    };
    load();
    const onDeviceChange = () => load();
    navigator.mediaDevices?.addEventListener?.('devicechange', onDeviceChange);
    return () => {
      cancelled = true;
      navigator.mediaDevices?.removeEventListener?.('devicechange', onDeviceChange);
    };
  }, []);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const options = useMemo(
    () => devices.map((device, index) => ({
      id: device.deviceId,
      name: device.label || `Microphone ${index + 1}`
    })),
    [devices]
  );

  const selectedName = useMemo(() => {
    if (options.length === 0) return 'No microphones found';
    return options.find((option) => option.id === selectedDeviceId)?.name || 'Select microphone';
  }, [options, selectedDeviceId]);

  return (
    <div
      className={`recording-mic-picker${disabled ? ' disabled' : ''}`}
      title={disabled ? disabledReason : 'Microphone'}
    >
      <FontAwesomeIcon icon={faMicrophone} className="recording-mic-picker-icon" />
      <CustomDropdown
        selected={selectedName}
        options={options}
        isOpen={isOpen && !disabled}
        toggleDropdown={() => {
          if (!disabled) setIsOpen((open) => !open);
        }}
        onSelect={(option) => {
          setIsOpen(false);
          if (option?.id) onSelectDevice?.(option.id);
        }}
        containerRef={dropdownRef}
        keyExtractor={(option) => option.id}
        disabled={disabled || options.length === 0}
        compact
      />
    </div>
  );
};

export default RecordingMicPicker;
